import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { digger } from '@cbre/digger';
import { FormControlOptions, Section } from '@miq/catalog';
import { DataObject } from '@shared/models/data-object.model';
import { cleanObject } from '@shared/utils/helpers/clean-object';
import { FormControlMaker } from '@controls/model/form-control-maker';

/**
 * This Service takes section formControlOptions and recursively register every level registered on it
 */
@Injectable({ providedIn: 'root' })
export class FormContainerControlBuilder implements FormControlMaker<Section> {
  constructor(protected fb: UntypedFormBuilder) {}

  /**
   * Creates a control depending on formControlOptions, returns null if no formControlOptions provided (no deep level)
   * @param section
   * @param form
   * @param data
   * @returns
   */

  public createForm(formControlOptions: FormControlOptions<Section>, dataEntry: DataObject) {
    const { controlsConfig, children } = formControlOptions;
    const formGroup = this.fb.group(controlsConfig || {});
    if (children != null) {
      this._makeChildrenFor(children, formGroup, dataEntry);
    }
    const cleanDataEntry = cleanObject(dataEntry);
    if (cleanDataEntry.children) {
      formGroup.setControl('children', this.fb.array([]));
    }
    formGroup.patchValue(cleanDataEntry, { emitEvent: false });
    return formGroup;
  }

  public makeControlEntry(
    section: Pick<Section, 'targetedControlOptions' | 'formControlOptions'>,
    form: UntypedFormGroup,
    data: DataObject
  ): AbstractControl {
    const { formControlOptions, targetedControlOptions } = section;
    let abstractControl: AbstractControl = null;
    if (targetedControlOptions) {
      const [firstControl] = Object.values(targetedControlOptions).map(fco => this._makeGroupEntry(fco, form, data));
      abstractControl = firstControl;
    } else if (formControlOptions) {
      abstractControl = this._makeGroupEntry(formControlOptions, form, data);
    }
    return abstractControl;
  }

  /**
   * Invokes form array or nested form creation depending on given formControlOptions
   * @param formControlOptions
   * @param form
   * @param data
   * @returns
   */
  private _makeGroupEntry(formControlOptions: FormControlOptions<Section>, form: UntypedFormGroup, data: DataObject) {
    const { type } = formControlOptions;
    let group: AbstractControl;
    if (type === 'array') {
      group = this._populateArrayEntries(formControlOptions, form, data);
    } else if (type === 'group') {
      group = this._populateNestedGroup(formControlOptions, form, data);
    }
    return group;
  }

  /**
   * Creates a form array entry
   * @param formControlOptions
   * @param form
   * @param data
   * @returns
   */
  private _populateArrayEntries(
    formControlOptions: FormControlOptions<Section>,
    form: UntypedFormGroup,
    data: DataObject
  ) {
    const { name, controlsConfig, children } = formControlOptions;
    // get or make form array control
    let formArray = form.get(name) as UntypedFormArray;
    if (!formArray) {
      formArray = this.fb.array([]);
      form.registerControl(name, formArray);
    }
    // get record entries for section control name
    // could it be one object instead of an array
    const entries = digger(data, name) || [];
    const dataEntries: DataObject[] = Array.isArray(entries) ? entries : [entries];

    // for each sub element in record, push a new array group entry
    dataEntries.forEach((dataEntry, idx) => {
      let entryGroup: UntypedFormGroup = formArray.at(idx) as UntypedFormGroup;
      if (!entryGroup) {
        entryGroup = this.fb.group(controlsConfig || {});
        if (children != null) {
          // create child controls before patching
          this._makeChildrenFor(children, entryGroup, dataEntry);
        }
        const cleanDataEntry = cleanObject(dataEntry);

        entryGroup.patchValue(cleanDataEntry, { emitEvent: false, onlySelf: true });
        formArray.push(entryGroup);
      } else if (controlsConfig) {
        this._ensureControlsConfig(entryGroup, Object.entries(controlsConfig));
      }
    });
    // return created control
    return formArray;
  }

  private _ensureControlsConfig(formGroup: UntypedFormGroup, controlConfigs: [string, any[]][]) {
    controlConfigs
      .filter(([controlName]) => formGroup.get(controlName) == null)
      .forEach(([controlName, controlConfig]) => {
        const [value, validators] = controlConfig;
        const control = this.fb.control(value, validators);
        formGroup.registerControl(controlName, control);
      });
  }

  /**
   * Creates a nested form group
   *
   * @param formControlOptions
   * @param form
   * @param data
   * @returns
   */
  private _populateNestedGroup(
    formControlOptions: FormControlOptions<Section>,
    form: UntypedFormGroup,
    data: DataObject
  ) {
    const { name, controlsConfig, children } = formControlOptions;
    let formGroup = form.get(name) as UntypedFormGroup;
    const dataEntry = digger(data, formControlOptions.name) || {};
    if (!formGroup) {
      // create if it does not exist
      formGroup = this.fb.group(controlsConfig || {});
      if (children != null) {
        // make children before patching
        this._makeChildrenFor(children, formGroup, dataEntry);
      }
      const cleanDataEntry = cleanObject(dataEntry);
      formGroup.patchValue(cleanDataEntry, { emitEvent: false });
      form.registerControl(name, formGroup);
    }
    return formGroup;
  }

  protected _makeChildrenFor(
    children: { [key: string]: FormControlOptions<Section> },
    formGroup: UntypedFormGroup,
    data: DataObject
  ) {
    Object.values(children).forEach(child => this._makeGroupEntry(child, formGroup, data));
  }
}
